// Le hors-ligne en lecture (lot 6.3) : ce que le service worker garde de chaque lecture utile, et le rangement des
// notes (les 20 dernières ouvertes). Le service worker et la page s'en servent tous deux : rien du DOM ici, seulement
// l'API Cache, qui existe des deux côtés.
//
// Ce qui est gardé est la dernière réponse du compte connecté, sur cet appareil : effacé à la déconnexion, quand un
// autre compte se connecte, ou si la personne désactive le hors-ligne (Réglages, téléphone partagé).

/** Le cache des réponses gardées. Changer de version efface l'ancien (une forme de réponse qui a changé). */
export const CACHE_DONNEES = 'alfred-donnees-v1';
/** Les réglages du service worker : il ne lit pas le stockage local de la page. */
const CACHE_REGLAGES = 'alfred-reglages';
const CLE_DESACTIVE = '/__alfred/hors-ligne-desactive';
const CLE_INDEX_NOTES = '/__alfred/notes-ouvertes';
/** L'en-tête où le service worker note quand une réponse a été gardée, et celui qui dit à la page qu'elle est hors ligne. */
export const ENTETE_GARDEE_LE = 'x-alfred-gardee-le';
export const ENTETE_HORS_LIGNE = 'x-alfred-hors-ligne';

/** Les notes gardées : les plus récemment ouvertes. */
export const MAX_NOTES = 20;

/**
 * Une lecture à garder ? `liste` : la dernière réponse, toujours ; `note` : une note ouverte (ou ses liens), dans la
 * limite des 20 dernières ; null : rien (une recherche, un réglage, l'assistant…).
 */
export function gardeDe(chemin: string, recherche = ''): { genre: 'liste' } | { genre: 'note'; id: string } | null {
  if (['/api/moi', '/api/sante/etat', '/api/brief', '/api/agenda', '/api/rappels', '/api/notifications'].includes(chemin)) return { genre: 'liste' };
  if (chemin === '/api/demandes' && recherche === '?statut=en_attente') return { genre: 'liste' };
  if (chemin === '/api/notes') return recherche ? null : { genre: 'liste' };
  const note = /^\/api\/notes\/([^/]+)(\/liens)?$/.exec(chemin);
  if (note?.[1]) return { genre: 'note', id: decodeURIComponent(note[1]) };
  return null;
}

/** La note `id` vient d'être ouverte : elle passe en tête ; rend le nouvel ordre et les notes qui sortent. */
export function rangerNote(ordre: string[], id: string, max = MAX_NOTES): { ordre: string[]; sortantes: string[] } {
  const suivant = [id, ...ordre.filter((x) => x !== id)];
  return { ordre: suivant.slice(0, max), sortantes: suivant.slice(max) };
}

// --- le cache ----------------------------------------------------------------------------------------------------------

/** L'API Cache n'existe qu'en HTTPS (ou sur localhost) : ailleurs, pas de hors-ligne, et rien à effacer. */
const sansCache = () => typeof caches === 'undefined';

export async function horsLigneActif(): Promise<boolean> {
  if (sansCache()) return false;
  return !(await (await caches.open(CACHE_REGLAGES)).match(CLE_DESACTIVE));
}

/** Désactiver efface aussi ce qui était gardé ; réactiver laisse le cache se remplir à nouveau. */
export async function reglerHorsLigne(actif: boolean): Promise<void> {
  if (sansCache()) return;
  const reglages = await caches.open(CACHE_REGLAGES);
  if (actif) await reglages.delete(CLE_DESACTIVE);
  else {
    await reglages.put(CLE_DESACTIVE, new Response('non'));
    await effacerDonnees();
  }
}

export async function effacerDonnees(): Promise<void> {
  if (sansCache()) return;
  await caches.delete(CACHE_DONNEES);
}

/** Garde une réponse réussie, datée ; pour une note, range l'index et oublie celles qui sortent des 20. */
export async function garder(requete: Request, reponse: Response, garde: NonNullable<ReturnType<typeof gardeDe>>, maintenant = new Date()): Promise<void> {
  const cache = await caches.open(CACHE_DONNEES);
  const entetes = new Headers(reponse.headers);
  entetes.set(ENTETE_GARDEE_LE, maintenant.toISOString());
  await cache.put(requete, new Response(await reponse.blob(), { status: reponse.status, statusText: reponse.statusText, headers: entetes }));
  if (garde.genre !== 'note') return;
  const index = await cache.match(CLE_INDEX_NOTES);
  const ordre = index ? ((await index.json()) as string[]) : [];
  const { ordre: suivant, sortantes } = rangerNote(ordre, garde.id);
  await cache.put(CLE_INDEX_NOTES, new Response(JSON.stringify(suivant), { headers: { 'content-type': 'application/json' } }));
  const origine = new URL(requete.url).origin;
  for (const id of sortantes) {
    await cache.delete(`${origine}/api/notes/${encodeURIComponent(id)}`);
    await cache.delete(`${origine}/api/notes/${encodeURIComponent(id)}/liens`);
  }
}

/** La réponse gardée, marquée « hors ligne » avec sa date ; ou une 503 qui le dit, si rien n'a été gardé. */
export async function relire(requete: Request): Promise<Response> {
  const copie = await (await caches.open(CACHE_DONNEES)).match(requete);
  if (!copie) {
    return new Response(JSON.stringify({ error: 'Hors ligne : rien de gardé pour cet écran sur cet appareil.' }), {
      status: 503, headers: { 'content-type': 'application/json', [ENTETE_HORS_LIGNE]: '' },
    });
  }
  const entetes = new Headers(copie.headers);
  entetes.set(ENTETE_HORS_LIGNE, copie.headers.get(ENTETE_GARDEE_LE) ?? '');
  return new Response(copie.body, { status: copie.status, statusText: copie.statusText, headers: entetes });
}
